import React, { useState } from "react";
import { Facebook, Twitter, Link2, MessageCircle, Share2, Check } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { trackEvent } from "@/lib/analytics";
import { socialLinks, canNativeShare, nativeShare, copyText } from "@/lib/share";

const CAPTION = {
  ar: (n) => `اكتشف ${n} في أطلس العالم العربي التفاعلي 🌍`,
  en: (n) => `Discover ${n} in the Interactive Arab World Atlas 🌍`,
  it: (n) => `Scopri ${n} nell'Atlante Interattivo del Mondo Arabo 🌍`,
  fr: (n) => `Découvrez ${n} dans l'Atlas Interactif du Monde Arabe 🌍`,
};

const NETWORKS = [
  { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, cls: "bg-[#25D366]/10 text-[#128C7E] hover:bg-[#25D366]/20 border-[#25D366]/30" },
  { id: "facebook", label: "Facebook", Icon: Facebook, cls: "bg-[#1877F2]/10 text-[#1877F2] hover:bg-[#1877F2]/20 border-[#1877F2]/30" },
  { id: "twitter", label: "X / Twitter", Icon: Twitter, cls: "bg-ink/5 text-ink hover:bg-ink/10 border-ink/20" },
  { id: "telegram", label: "Telegram", Icon: Share2, cls: "bg-[#229ED9]/10 text-[#229ED9] hover:bg-[#229ED9]/20 border-[#229ED9]/30" },
];

/** Social share buttons for a single country (deep link `?c=<CODE>`, OG page `/api/share/<CODE>`). */
export default function ShareButtons({ country }) {
  const { t, lang } = useLang();
  const [copied, setCopied] = useState(false);
  if (!country) return null;

  const url = `${window.location.origin}/?c=${country.code}&lang=${lang}`;
  const shareUrl = `${window.location.origin}/api/share/${country.code}?lang=${lang}`;
  const name = country.names[lang] || country.names.en;
  const caption = (CAPTION[lang] || CAPTION.en)(name);
  const links = socialLinks(shareUrl, caption);
  const track = (method) => trackEvent("share", { method, content_type: "country", item_id: country.code, lang });

  const copyLink = async () => {
    track("copy_link");
    await copyText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      {NETWORKS.map(({ id, label, Icon, cls }) => (
        <a key={id} href={links[id]} target="_blank" rel="noopener noreferrer" aria-label={label} onClick={() => track(id)}
          className={`w-9 h-9 inline-flex items-center justify-center rounded-full border transition-colors ${cls}`} data-testid={`share-${id}`}>
          <Icon className="w-4 h-4" />
        </a>
      ))}
      <button onClick={copyLink} aria-label={copied ? "Copied" : "Copy link"} data-testid="share-copy"
        className={`w-9 h-9 inline-flex items-center justify-center rounded-full border transition-colors ${copied ? "bg-oasis/15 text-oasis border-oasis/40" : "bg-sandDeep/30 text-ink hover:bg-sandDeep/60 border-stoneLine"}`}>
        {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </button>
      {canNativeShare() && (
        <button onClick={() => { track("native"); nativeShare({ title: name, text: caption, url: shareUrl }); }} aria-label={t("share")} data-testid="share-native"
          className="inline-flex w-9 h-9 items-center justify-center rounded-full border border-stoneLine bg-ink text-sand hover:bg-ochre transition-colors">
          <Share2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
